import { DomUtils } from '@mprep/shared/lib/helpers'

import {
  ElementRef,
  Renderer2,
  Directive,
  AfterViewInit,
  AfterViewChecked,
  HostListener,
  Input
} from '@angular/core'
import { BS_SCREEN_SIZES } from './../'

@Directive({
  selector: '[mpStickyElement]'
})
export class StickyElementDirective implements AfterViewInit, AfterViewChecked {

  private _initialElementPositionY: number
  private _initialElementPositionYWithMargin: number
  private _lastVerticalScrollPosition: number
  private _lastTopPosition: number
  private _domElement: HTMLElement
  private _lastDomElementHeight: number
  private _containerElement: HTMLElement
  private _stickyTopOffset: number
  private _originalStyle: CSSStyleDeclaration

  @Input()
  public activeMinBreakPoint: string

  constructor(element: ElementRef, private _renderer: Renderer2, private _domUtils: DomUtils) {
    this._domElement = element.nativeElement
    this._originalStyle = <CSSStyleDeclaration> Object.assign({}, this._domElement.style)
    this._stickyTopOffset = 15
  }

  public ngAfterViewInit(): void {
    this._containerElement = this._domUtils.findParentBy(this._domElement, {tagName: 'body'})
    this._initialElementPositionY = this._domUtils.getElementPosition(this._domElement).y
    this._initialElementPositionYWithMargin = this._domUtils.getElementPositionWithMargin(this._domElement).y
    this._lastVerticalScrollPosition = 0
    this._run()
  }

  public ngAfterViewChecked() {
    // This method runs after every angular change detection run, i.e.:
    // if changes had been made, we'll be able to detect changes in the element's size
    const elementHeightChanged = this._lastDomElementHeight !== this._getElementHeight()
    if (elementHeightChanged) {
      this._makeContainerFitElementHeight()
      this._lastDomElementHeight = this._getElementHeight()
    }
  }

  @HostListener('window:scroll',  ['$event'])
  public onScroll($event) {
    this._run()
  }

  @HostListener('window:resize',  ['$event'])
  public onResize($event) {
    this._run()
  }

  private _run() {
    if (this._enabled()) {
      this._updatePostion()
    } else {
      this._restoreElement()
    }
  }

  private _enabled(): boolean {
    if (typeof this.activeMinBreakPoint === 'undefined') { return true }
    const size: number = BS_SCREEN_SIZES[this.activeMinBreakPoint.toLowerCase()]
    if (typeof size !== 'number') { return true }
    return window.innerWidth >= size
  }

  private _restoreElement() {
    this._domElement.style.position = this._originalStyle.position
    this._domElement.style.top = this._originalStyle.top
  }

  private _getElementHeight(): number {
    return this._domUtils.getElementHeight(this._domElement)
  }

  private _updatePostion(): void {
    // before anything, make sure position is fixed
    this._renderer.setStyle(this._domElement, 'position', 'fixed')

    const domElement = this._domElement
    const windowHeight = this._domUtils.getWindowHeight()
    const vScrollPosition = this._domUtils.getScrollPosition().y
    const vScrollChange = vScrollPosition - this._lastVerticalScrollPosition
    const elementMargin = this._initialElementPositionYWithMargin - this._initialElementPositionY
    let updatedPosition = this._initialElementPositionYWithMargin

    // otherwise: it needs to become sticky
    if (vScrollPosition > this._initialElementPositionY) {
      const elementIsLargerThanViewport = this._domUtils.getElementHeight(domElement) >= windowHeight
      // if the element does not fit on the viewport scrolling moves the menu by the `vScrollChange`
      updatedPosition = elementIsLargerThanViewport
        ? this._calculateOverflowedVScroll(domElement, windowHeight, vScrollPosition, vScrollChange)
        : vScrollPosition // by default: place it at the top
      // Account for the elements margin
      updatedPosition += elementMargin
      // Add the distance from the top once the element is sticky
      updatedPosition += this._stickyTopOffset
    }

    this._lastVerticalScrollPosition = vScrollPosition

    let newTopPosition = updatedPosition - vScrollPosition
    if (this._lastTopPosition !== newTopPosition) {
      this._setTop(newTopPosition)
      this._lastTopPosition = newTopPosition
    }
  }

  private _calculateOverflowedVScroll(domElement, windowHeight, vScrollPosition, vScrollChange): number {
    const elementHeight = this._domUtils.getElementHeight(domElement)
    let calculatedPosition = this._domUtils.getElementPosition(domElement).y - vScrollChange

    const isScrollDown = vScrollChange > 0
    if (isScrollDown) {
      let offsetBottomPostion = windowHeight - elementHeight + vScrollPosition
      calculatedPosition = Math.max(calculatedPosition, offsetBottomPostion)
    } else {
      calculatedPosition = Math.min(calculatedPosition, vScrollPosition)
    }
    return calculatedPosition
  }

  private _makeContainerFitElementHeight() {
    this._domUtils.setMinHeight(this._containerElement,
      this._domUtils.getElementHeight(this._domElement) + this._domUtils.getElementPosition(this._domElement).y)
  }

  private _setTop(top: number) {
    this._renderer.setStyle(this._domElement, 'top', `${top}px`)
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/directives/sticky-element.directive.ts